import { Component, Input, OnChanges, OnInit, ViewChild, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Client } from 'core/models/client';
import { Subscription as rxjsSubscription } from 'rxjs';
import { Subscription } from 'core/models/subscription';
import { ListComponent, ModalComponent } from 'core/components';
import { SubscriptionsService, PopUpService } from 'core/services';
import { StoreService } from 'core/services/store.service';
import { CreditsListService } from './credits-list.service';

@Component({
  selector: 'lms-credits-list',
  templateUrl: './credits-list.component.html',
  styleUrls: ['./credits-list.component.scss'],
  providers: [CreditsListService]
})
export class CreditsListComponent implements OnInit, OnChanges {
  @Input() listId: string;
  @Input() client: Client;
  @Input() type: string;
  @Input() updatedCredits: boolean;
  @Input() options: any;
  @Output() subscriptionChanged: EventEmitter<any> = new EventEmitter<any>();
  public filters: any;
  public defaultOptions: any = {
    aggregates: 'valid'
  };
  public listOptions: any;
  public selectedSubscription: Subscription;
  public timezone: string;
  public dataSource: any;
  @ViewChild(ListComponent, { static: false }) public listComponent: ListComponent;
  @ViewChild(ModalComponent, { static: false }) public modalComponent: ModalComponent;

  subscriptions: Subscription[];
  actionsSubscription: rxjsSubscription;

  constructor(
    public creditsListService: CreditsListService,
    public subscriptionsService: SubscriptionsService,
    public router: Router,
    public route: ActivatedRoute,
    public translate: TranslateService,
    private popupService: PopUpService
  ) {}

  ngOnInit() {
    this.actionsSubscription = this.creditsListService
      .getActionEmitter()
      .subscribe((event) => this.onListAction(event));
    this.route.data.subscribe((data) => {
      if (!this.client && data.client) {
        this.client = data['client'];
      }
    });
    this.initializeDatasource();
  }

  async ngOnChanges() {
    this.initializeDatasource();
  }

  async initializeDatasource() {
    this.filters = { clientId: this.client.id, type: this.type };
    this.listOptions = Object.assign(
      { filters: this.filters, timezone: this.client.timezone, clientType: this.client.type },
      this.defaultOptions,
      this.options
    );
    this.dataSource = await this.creditsListService.getDatasource(this.listId, this.listOptions);
    if (this.client) {
      this.timezone = this.client.timezone;
    }
  }

  ngOnDestroy() {
    this.actionsSubscription.unsubscribe();
  }

  onListAction(event) {
    const { action, subscription } = event;
    if (action === 'delete') {
      this.deleteSubscription(subscription);
    }
    if (action === 'edit') {
      this.editSubscription(subscription);
    }
  }

  setCollection(collection) {
    this.subscriptions = collection;
  }

  onDatasourceUpdate(dataSource) {
    this.dataSource = dataSource;
    StoreService.update(this.listId, this.dataSource);
  }

  deleteSubscription(subscription: Subscription) {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('credits.confirmDelete', {
          creditsBag: subscription.id
        }),
        text: this.translate.instant('credits.deleteMessage'),
        size: 'small'
      })
      .then(() => this.subscriptionsService.delete(subscription.id))
      .then(() => this.onSubscriptionChanged(subscription))
      .catch((err) => {
        if (err && err !== 'cancel') {
          const translatedErrorLabel = 'credits.delete.errors.' + err.errorCode;
          const translatedError = err.errorCode ? this.translate.instant(translatedErrorLabel) : err;

          this.popupService.showError({
            title: this.translate.instant('credits.deleteError'),
            text: translatedErrorLabel === translatedError ? err.serverMessage || err : translatedError
          });
        }
      });
  }

  onUpdateSubscription(subscription: Subscription) {
    this.modalComponent.close();
    this.selectedSubscription = null;
    this.onSubscriptionChanged(subscription);
  }

  onSubscriptionChanged(subscription: Subscription) {
    this.refreshTable();
    this.subscriptionChanged.emit(subscription);
  }

  refreshTable() {
    this.dataSource.refreshData();
    StoreService.update(this.listId, this.dataSource);
  }

  editSubscription(creditsBag: Subscription) {
    this.selectedSubscription = { ...creditsBag };
    this.modalComponent.open();
  }

  cancelEditSubscription() {
    this.modalComponent.close();
    this.selectedSubscription = null;
  }

  isExpired(subscription: Subscription): boolean {
    if (!subscription.endDate) {
      // Valid will be false when credits bag was transferred from a partner without a valid plan
      return !subscription.valid;
    } else {
      return new Date(subscription.endDate).getTime() < Date.now();
    }
  }

  nextExpires(subscription: Subscription): boolean {
    const diffTime = (new Date(subscription.endDate).getTime() - Date.now()) / (1000 * 3600 * 24);
    const availableCredits = subscription.rateLimit - subscription.rateConsumed;
    return diffTime <= 90 && diffTime >= 0 && availableCredits > 0 ? true : false;
  }
}
